import { useState, useEffect } from 'react';
import { X, Users, Calendar, DollarSign, AlertCircle, RefreshCw, Database } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './Button';
import { getAvailableEvents, isTeamEvent, isSlackEvent, supabase, type EventAvailability } from '../lib/supabase';

interface EventAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EventAvailabilityModal = ({ isOpen, onClose }: EventAvailabilityModalProps) => {
  const [events, setEvents] = useState<EventAvailability[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isOfflineMode, setIsOfflineMode] = useState(false);

  const loadEvents = async () => {
    setLoading(true);
    setError('');
    setIsOfflineMode(false);
    console.log('Loading events in EventAvailabilityModal...');
    
    try {
      const eventsData = await getAvailableEvents();
      console.log('Events loaded successfully:', eventsData);
      setEvents(eventsData);
      
      // Check if we're using mock data (indicates offline mode)
      if (!supabase) {
        setIsOfflineMode(true);
      }
    } catch (err) {
      console.error('Error loading events in modal:', err);
      
      // Provide a more user-friendly error message
      let errorMessage = 'Failed to load event data. Please try again.';
      
      if (err instanceof Error) {
        if (err.message.includes('connection') || err.message.includes('fetch')) {
          errorMessage = 'Unable to connect to the database. Showing sample data for demonstration.';
          setIsOfflineMode(true);
        } else if (err.message.includes('permission')) {
          errorMessage = 'Unable to access event data. Please try refreshing the page.';
        } else {
          errorMessage = `Error: ${err.message}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened, loading events...');
      loadEvents();
    }
  }, [isOpen]);

  // Group events by type
  const eventsByType = events.reduce((acc, event) => {
    if (!acc[event.event_type]) acc[event.event_type] = [];
    acc[event.event_type].push(event);
    return acc;
  }, {} as Record<string, EventAvailability[]>);

  const getAvailabilityColor = (event: EventAvailability) => {
    if (isSlackEvent(event.event_type)) return 'text-green-600';
    if (event.is_full) return 'text-red-600';
    if (event.available_spots <= 2) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getAvailabilityBadge = (event: EventAvailability) => {
    if (isSlackEvent(event.event_type)) return 'UNLIMITED';
    if (event.is_full) return 'FULL';
    if (event.available_spots <= 2) return 'LIMITED';
    return 'AVAILABLE';
  };

  const getBadgeColor = (event: EventAvailability) => {
    if (isSlackEvent(event.event_type)) return 'bg-blue-100 text-blue-800';
    if (event.is_full) return 'bg-red-100 text-red-800';
    if (event.available_spots <= 2) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const formatAvailableSpots = (event: EventAvailability) => {
    if (isSlackEvent(event.event_type)) {
      return 'Unlimited';
    }
    if (isTeamEvent(event.event_name)) {
      const teams = Math.floor(event.available_spots / 2);
      return `${event.available_spots} people (${teams} teams)`;
    }
    return event.available_spots.toString();
  };

  const formatCapacity = (event: EventAvailability) => {
    if (isSlackEvent(event.event_type)) {
      return 'Unlimited';
    }
    if (isTeamEvent(event.event_name)) {
      const teams = Math.floor(event.max_capacity / 2);
      return `${event.max_capacity} people (${teams} teams)`;
    }
    return event.max_capacity.toString();
  };

  const formatPrice = (event: EventAvailability) => {
    const basePrice = `$${event.price.toFixed(2)}`;
    if (isTeamEvent(event.event_name)) {
      return `${basePrice} per partner`;
    }
    return basePrice;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-heading font-bold text-primary-900 flex items-center">
                  <Calendar size={24} className="mr-2" />
                  Event Availability
                  {isOfflineMode && (
                    <span className="ml-2 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full flex items-center">
                      <Database size={12} className="mr-1" />
                      DEMO MODE
                    </span>
                  )}
                </h2>
                <p className="text-gray-600 mt-1">
                  {isOfflineMode 
                    ? 'Showing sample data - Database connection unavailable'
                    : 'Real-time availability for all rodeo events'
                  }
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadEvents}
                  disabled={loading}
                  icon={<RefreshCw size={16} className={loading ? 'animate-spin' : ''} />}
                >
                  Refresh
                </Button>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Close modal"
                >
                  <X size={24} className="text-gray-500" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center space-x-2">
                    <RefreshCw size={20} className="animate-spin text-primary-600" />
                    <span className="text-gray-600">Loading events...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertCircle size={48} className={isOfflineMode ? "text-yellow-500" : "text-red-500"} mb-4 />
                  <p className={`text-center mb-4 max-w-md ${isOfflineMode ? "text-yellow-600" : "text-red-600"}`}>
                    {error}
                  </p>
                  {!isOfflineMode && (
                    <Button variant="outline" onClick={loadEvents}>
                      Try Again
                    </Button>
                  )}
                  {isOfflineMode && (
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">
                        This is demonstration data showing how the system works.
                      </p>
                      <Button variant="outline" onClick={loadEvents}>
                        Try Connection Again
                      </Button>
                    </div>
                  )}
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">No events available at this time.</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {isOfflineMode && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center">
                        <Database className="text-yellow-600 mr-2" size={20} />
                        <div>
                          <h3 className="text-yellow-800 font-semibold">Demo Mode Active</h3>
                          <p className="text-yellow-700 text-sm mt-1">
                            The database is currently unavailable, so we're showing sample event data. 
                            The actual system would display real-time availability when connected.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {Object.entries(eventsByType).map(([eventType, typeEvents]) => (
                    <div key={eventType}>
                      <h3 className="text-xl font-heading font-semibold text-primary-800 mb-4 flex items-center">
                        <Calendar size={20} className="mr-2" />
                        {eventType}
                        {isSlackEvent(eventType) && (
                          <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            UNLIMITED CAPACITY
                          </span>
                        )}
                      </h3>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Event Name
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Entry Fee
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Capacity
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Registered
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Available
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {typeEvents.map((event) => (
                              <tr key={event.id} className="hover:bg-gray-50">
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">
                                    {event.event_name}
                                    {isTeamEvent(event.event_name) && (
                                      <span className="ml-2 px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                                        TEAM EVENT
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <div className="flex items-center text-sm text-gray-900">
                                    <DollarSign size={14} className="mr-1" />
                                    {formatPrice(event)}
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <div className="flex items-center text-sm text-gray-900">
                                    <Users size={14} className="mr-1" />
                                    {formatCapacity(event)}
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {event.current_registrations}
                                    {isTeamEvent(event.event_name) && (
                                      <span className="text-xs text-gray-500 ml-1">
                                        ({Math.floor(event.current_registrations / 2)} teams)
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <div className={`text-sm font-medium ${getAvailabilityColor(event)}`}>
                                    {formatAvailableSpots(event)}
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getBadgeColor(event)}`}>
                                    {getAvailabilityBadge(event)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                <div className="flex items-center space-x-4 flex-wrap gap-2">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span>Available</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                    <span>Limited (≤2 spots)</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                    <span>Full</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    <span>Unlimited</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                    <span>Team Event (price per partner)</span>
                  </div>
                </div>
              </div>
              <Button variant="primary" onClick={onClose}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EventAvailabilityModal;